import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Users, Building2, Search, Mail, MapPin, Briefcase,
  UserCheck, Shield, Plus, Pencil, Trash2, Loader2, MoreVertical, UserPlus,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { UserAvatar } from '@/components/UserAvatar';
import { PageBanner } from '@/components/PageBanner';
import { useConfirm } from '@/components/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';
import { KpiCard } from '@/components/KpiCard';
import { PageShell } from '@/components/PageShell';
import { PageHeader } from '@/components/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { GroupBadges } from '@/components/GroupBadges';

// Consistent badge color for all content team roles
const ROLE_BADGE_CLASS = 'bg-primary/10 text-primary  ';
const GROUP_COLOR_OPTIONS = [
  '#3b82f6',
  '#8b5cf6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#64748b',
];
const ALL_EMPLOYEES_GROUP_COLOR = '#64748b';
const DEFAULT_GROUP_COLOR = GROUP_COLOR_OPTIONS[0];

const isAllEmployeesGroup = (group) => {
  const slug = String(group?.slug || '').toLowerCase();
  const id = String(group?.id || '').toLowerCase();
  const name = String(group?.name || '').trim().toLowerCase();
  return slug === 'all-employees' || id === 'all' || name === 'all employees';
};

const getGroupShieldColor = (group) => (
  isAllEmployeesGroup(group) ? ALL_EMPLOYEES_GROUP_COLOR : (group?.color || DEFAULT_GROUP_COLOR)
);

export default function UserGroupsPage() {
  const { hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]); // Members of selected group
  const [loading, setLoading] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userTab, setUserTab] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [userPage, setUserPage] = useState(1);
  const [userRowsPerPage, setUserRowsPerPage] = useState(6);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [groupFormError, setGroupFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [ConfirmEl, confirm] = useConfirm();
  const isAdmin = hasRole(['admin']);
  // EIP-275: approvers can manage groups + membership, but deleting tenant users
  // (the roster) stays admin-only, so those controls still gate on isAdmin.
  const canManageGroups = hasRole(['admin', 'approver']);
  const selectedGroup = selectedGroupId ? groups.find(g => g.id === selectedGroupId) : null;

  const fetchDataInner = useCallback(async () => {
    const [usersRes, groupsRes] = await Promise.all([
      api.get('/groups/users'),
      api.get('/groups'),
    ]);
    setUsers(usersRes.data || []);
    setGroups(groupsRes.data || []);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await fetchDataInner();
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setGroups([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [fetchDataInner]);

  const refetchData = () => fetchDataInner().catch(err => console.error('Background refetch failed:', err));

  const adjustMemberCount = (groupId, delta) => {
    setGroups(prev => prev.map(g => (
      g.id === groupId
        ? { ...g, member_count: Math.max(0, (g.member_count || 0) + delta) }
        : g
    )));
  };

  // Fetch group members when a group is selected
  const fetchGroupMembers = async (groupId) => {
    setLoadingMembers(true);
    try {
      const res = await api.get(`/groups/${groupId}/members`);
      setGroupMembers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch group members:', err);
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Handle group selection
  const handleSelectGroup = async (group) => {
    setSelectedGroupId(group.id);
    await fetchGroupMembers(group.id);
  };

  useEffect(() => { 
    fetchData(); 
  }, [fetchData]);

  useEffect(() => {
    setUserPage(1);
  }, [searchQuery, userTab, departmentFilter, countryFilter, selectedGroupId]);

  if (!canManageGroups) {
    return (
      <PageShell maxWidth="7xl">
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="p-6 flex items-center gap-4">
            <Shield className="h-8 w-8 text-warning" />
            <div>
              <h3 className="font-semibold">Access Denied</h3>
              <p className="text-muted-foreground">Only admins and approvers can manage groups.</p>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', description: '', color: DEFAULT_GROUP_COLOR });
    setGroupFormError('');
    setGroupDialogOpen(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, description: group.description || '', color: getGroupShieldColor(group) });
    setGroupDialogOpen(true);
  };

  // Clear the form error whenever the dialog closes so a stale message
  // (e.g. a failed-create slug warning) can't reappear when reopening to edit.
  const handleGroupDialogOpenChange = (open) => {
    setGroupDialogOpen(open);
    if (!open) {
      setGroupFormError('');
    }
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) {
      toast.error('Error', { description: 'Group name is required' });
      return;
    }
    
    setSaving(true);
    setGroupFormError('');
    try {
      if (editingGroup) {
        await api.put(`/groups/${editingGroup.id}`, groupForm);
        toast.success('Group Updated', { description: `${groupForm.name} has been updated.` });
      } else {
        await api.post('/groups', groupForm);
        toast.success('Group Created', { description: `${groupForm.name} has been created.` });
      }
      setGroupDialogOpen(false);
      fetchData();
    } catch (err) {
      const message = err.response?.data?.detail || err.message;
      setGroupFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    if (group.is_default) {
      toast.error('Cannot Delete', { description: 'Default groups cannot be deleted.' });
      return;
    }
    
    const ok = await confirm({ title: 'Delete Group', description: `Are you sure you want to delete "${group.name}"?`, confirmLabel: 'Delete', variant: 'delete' });
    if (!ok) return;
    
    try {
      await api.delete(`/groups/${group.id}`);
      toast.success('Group Deleted', { description: `${group.name} has been deleted.` });
      if (selectedGroupId === group.id) setSelectedGroupId(null);
      fetchData();
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || err.message });
    }
  };

  const handleDeleteUser = async (user) => {
    // Users with content team roles should be deleted from the Team Management page
    // Check if user has content team roles (admin, analyst, viewer, approver)
    if (user.roles && (user.roles.includes('admin') || user.roles.includes('analyst') || user.roles.includes('viewer') || user.roles.includes('approver'))) {
      toast.error('Cannot Delete', { description: 'Content team members must be removed from the Team Management page.' });
      return;
    }
    
    if (user.source === 'eva_user') {
      toast.error('Cannot Delete', { description: 'Users who have interacted with Eva cannot be deleted, only removed from groups.' });
      return;
    }
    
    const ok = await confirm({ title: 'Remove User', description: `Are you sure you want to remove ${user.name || user.email}? This will delete the user and remove them from all groups.`, confirmLabel: 'Remove', variant: 'delete' });
    if (!ok) return;
    
    try {
      await api.delete(`/groups/users/${user.user_id || user.id}`);
      toast.success('User Removed', { description: `${user.name || user.email} has been removed.` });
      fetchData();
      // Refresh group members if a group is selected
      if (selectedGroup) {
        fetchGroupMembers(selectedGroup.id);
      }
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || 'Failed to remove user' });
    }
  };

  const handleAddMember = async (user) => {
    if (!selectedGroup) return;
    try {
      const memberId = user.user_id || user.id;
      await api.post(`/groups/${selectedGroup.id}/members?user_id=${memberId}`);
      toast.success('Member Added', { description: `${user.first_name || user.email} added to ${selectedGroup.name}.` });
      // The user's `groups` array was fetched before this add, so it doesn't yet
      // include selectedGroup — merge it in optimistically or the badge for the
      // group just added would flash missing until fetchGroupMembers resolves.
      const optimisticGroups = (user.groups || []).some(g => g.id === selectedGroup.id)
        ? (user.groups || [])
        : [...(user.groups || []), { id: selectedGroup.id, name: selectedGroup.name, slug: selectedGroup.slug, color: selectedGroup.color }];
      setGroupMembers(prev => (
        prev.some(member => (member.user_id || member.id) === memberId)
          ? prev
          : [...prev, { ...user, user_id: memberId, groups: optimisticGroups }]
      ));
      adjustMemberCount(selectedGroup.id, 1);
      fetchGroupMembers(selectedGroup.id);
      refetchData();
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || 'Failed to add member' });
    }
  };

  const handleRemoveMember = async (user) => {
    if (!selectedGroup) return;
    try {
      const memberId = user.user_id || user.id;
      await api.delete(`/groups/${selectedGroup.id}/members/${memberId}`);
      toast.success('Member Removed', { description: `${user.first_name || user.email} removed from ${selectedGroup.name}.` });
      setGroupMembers(prev => prev.filter(member => (member.user_id || member.id) !== memberId));
      adjustMemberCount(selectedGroup.id, -1);
      fetchGroupMembers(selectedGroup.id);
      refetchData();
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || 'Failed to remove member' });
    }
  };

  // Users not already in the selected group (for the add member dialog)
  const availableMembers = selectedGroup
    ? users.filter(u => {
        const uid = u.user_id || u.id;
        const isMember = groupMembers.some(m => (m.user_id || m.id) === uid);
        const matchesSearch = !addMemberSearch ||
          (u.first_name || '').toLowerCase().includes(addMemberSearch.toLowerCase()) ||
          (u.last_name || '').toLowerCase().includes(addMemberSearch.toLowerCase()) ||
          (u.email || '').toLowerCase().includes(addMemberSearch.toLowerCase());
        return !isMember && matchesSearch;
      })
    : [];

  const departments = [...new Set(users.map(u => u.department).filter(Boolean))].sort();
  const countries = [...new Set(users.map(u => u.country || u.office_location).filter(Boolean))].sort();

  const matchesUserSearch = (user) => {
    const query = searchQuery.toLowerCase();
    if (!query) return true;
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query) ||
      user.department?.toLowerCase().includes(query)
    );
  };

  const matchesUserFilters = (user) => {
    const matchesTab =
      userTab === 'all' ||
      (userTab === 'content_team' && user.is_content_team) ||
      (userTab === 'employees' && !user.is_content_team);
    const matchesDepartment = departmentFilter === 'all' || user.department === departmentFilter;
    const userCountry = user.country || user.office_location;
    const matchesCountry = countryFilter === 'all' || userCountry === countryFilter;
    return matchesTab && matchesDepartment && matchesCountry;
  };

  // When a group is selected, use groupMembers; otherwise show all users
  const baseUsers = selectedGroup ? groupMembers : users;
  const displayUsers = baseUsers.filter(u => matchesUserSearch(u) && matchesUserFilters(u));
  const userPageCount = Math.max(1, Math.ceil(displayUsers.length / userRowsPerPage));
  const normalizedUserPage = Math.min(userPage, userPageCount);
  const pagedUsers = displayUsers.slice(
    (normalizedUserPage - 1) * userRowsPerPage,
    normalizedUserPage * userRowsPerPage
  );

  const rangeLabel = (page, rowsPerPage, total) => {
    if (total === 0) return 'Showing 0 of 0';
    const start = (page - 1) * rowsPerPage + 1;
    const end = Math.min(page * rowsPerPage, total);
    return `Showing ${start}-${end} of ${total}`;
  };

  const hasUsers = users.length > 0;
  const hasGroups = groups.length > 0;

  return (
    <PageShell maxWidth="7xl" data-testid="employees-page">
      {ConfirmEl}
      {/* Page Banner */}
      <PageBanner pageKey="people-groups" />
      
      <PageHeader
        title="Groups"
        description="Manage user groups for document visibility and Eva content targeting"
        actions={(
        <Button onClick={handleCreateGroup} className="bg-primary text-primary-foreground hover:bg-primary/90 sm:self-auto">
          <Plus className="w-4 h-4 mr-2" />
          Create Group
        </Button>
        )}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard title="Total Users" value={users.length} icon={Users} color="info" />
        <KpiCard title="Groups" value={groups.length} icon={Shield} color="primary" />
        <KpiCard title="Default Groups" value={groups.filter(g => g.is_default).length} icon={UserCheck} color="success" />
        <KpiCard title="Custom Groups" value={groups.filter(g => !g.is_default).length} icon={Building2} color="warning" />
      </div>

      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-3">
        {/* Groups List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-foreground">Groups</CardTitle>
              {selectedGroup && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedGroupId(null);
                    setGroupMembers([]);
                  }}
                >
                  All users
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <div className="space-y-2" data-testid="groups-list-loading">
                {[1, 2, 3, 4].map((row) => (
                  <Skeleton key={row} className="h-16 w-full rounded-xl" />
                ))}
              </div>
            ) : !hasGroups ? (
              <EmptyState
                icon={Shield}
                title="No groups configured yet"
                description="Create a group to manage document visibility and content targeting."
                actionLabel="Create group"
                actionIcon={Plus}
                onAction={handleCreateGroup}
                variant="plain"
                compact
              />
            ) : (
              groups.map((group) => (
                <div
                  key={group.id}
                  onClick={() => handleSelectGroup(group)}
                  className={`p-3 rounded-xl cursor-pointer transition-all group ${
                    selectedGroup?.id === group.id
                      ? 'bg-info/10  border border-info/30 '
                      : 'hover:bg-secondary/80 dark:hover:bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl border bg-secondary"
                      style={{
                        color: getGroupShieldColor(group),
                        borderColor: `${getGroupShieldColor(group)}55`,
                      }}
                    >
                      <Shield className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">{group.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {group.member_count > 0 ? `${group.member_count} member${group.member_count !== 1 ? 's' : ''}` : 'No members'}
                        {(group.is_default === 1 || group.is_default === true) && ' · Default'}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        data-testid={`edit-group-${group.id}`}
                        onClick={(e) => { e.stopPropagation(); handleEditGroup(group); }}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      {!group.is_default ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group); }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        {/* Users List */}
        <Card className="self-start md:col-span-2 bg-card border-border">
          <CardHeader>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">
                  {selectedGroup ? `${selectedGroup.name} Members` : 'All Users'}
                </CardTitle>
                {selectedGroup?.description && (
                  <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    {selectedGroup.description}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {selectedGroup && (
                  <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => { setAddMemberSearch(''); setAddMemberOpen(true); }}>
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                )}
                <div className="relative w-full sm:w-48">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3 pt-2 lg:flex-row lg:items-center lg:justify-between">
              <Tabs value={userTab} onValueChange={setUserTab} className="w-full lg:w-auto">
                <TabsList className="grid w-full grid-cols-3 lg:w-[420px]">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="employees">Employees</TabsTrigger>
                  <TabsTrigger value="content_team">Content Team</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:w-[420px]">
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All departments</SelectItem>
                    {departments.map(department => (
                      <SelectItem key={department} value={department}>{department}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locations</SelectItem>
                    {countries.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading || loadingMembers ? (
              <div className="space-y-3" data-testid="groups-users-loading">
                {[1, 2, 3, 4, 5].map((row) => (
                  <Skeleton key={row} className="h-16 w-full rounded-xl" />
                ))}
              </div>
            ) : !hasUsers && !selectedGroup ? (
              <EmptyState
                icon={Users}
                title="No users yet"
                description="Users will appear here when they interact with Eva or are synced from your identity provider."
                variant="plain"
              />
            ) : displayUsers.length === 0 ? (
              <EmptyState
                icon={Search}
                title={selectedGroup ? `No matching members in ${selectedGroup.name}` : 'No users match these filters'}
                description="Try changing your search, role, department, or country filters."
                variant="plain"
              />
            ) : (
              <>
              <div className="space-y-2">
                {pagedUsers.map((user) => (
                  <div key={user.user_id || user.id || user.email} className="flex flex-col gap-3 rounded-xl p-3 transition-colors hover:bg-secondary/80 dark:hover:bg-secondary/50 sm:flex-row sm:items-center sm:justify-between group">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <UserAvatar user={user} size="md" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground">
                          {user.first_name && user.last_name 
                            ? `${user.first_name} ${user.last_name}` 
                            : user.name || 'Unknown User'}
                        </p>
                        <p className="flex items-center gap-1 break-all text-sm text-muted-foreground">
                          <Mail className="w-3 h-3" /> {user.email}
                        </p>
                        {user.job_title && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Briefcase className="w-3 h-3" /> {user.job_title}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      {user.is_content_team && (
                        <Badge className={`text-xs ${ROLE_BADGE_CLASS}`}>Content Team</Badge>
                      )}
                      {user.department && (
                        <Badge variant="outline" className="text-xs">{user.department}</Badge>
                      )}
                      {user.office_location && (
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {user.office_location}
                        </Badge>
                      )}
                      {user.country && (
                        <Badge variant="secondary" className="text-xs">{user.country}</Badge>
                      )}
                      <GroupBadges groups={user.groups} maxVisible={2} hideWhenEmpty />
                      {selectedGroup ? (
                        !isAllEmployeesGroup(selectedGroup) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-destructive hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleRemoveMember(user)}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Remove
                          </Button>
                        )
                      ) : (
                        // EIP-275: deleting a tenant user stays admin-only; approvers
                        // manage groups + membership but not the user roster.
                        isAdmin && !user.is_content_team && user.source !== 'eva_user' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`user-actions-${user.user_id || user.id}`}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeleteUser(user)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remove User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-col gap-3 border-t pt-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                <span>{rangeLabel(normalizedUserPage, userRowsPerPage, displayUsers.length)}</span>
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  <Select value={String(userRowsPerPage)} onValueChange={(value) => { setUserRowsPerPage(Number(value)); setUserPage(1); }}>
                    <SelectTrigger className="h-9 w-[92px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[6, 10, 25].map(size => (
                        <SelectItem key={size} value={String(size)}>{size} rows</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" disabled={normalizedUserPage <= 1} onClick={() => setUserPage(page => Math.max(1, page - 1))}>
                    Previous
                  </Button>
                  <span>Page {normalizedUserPage} of {userPageCount}</span>
                  <Button variant="outline" size="sm" disabled={normalizedUserPage >= userPageCount} onClick={() => setUserPage(page => Math.min(userPageCount, page + 1))}>
                    Next
                  </Button>
                </div>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={handleGroupDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Group' : 'Create New Group'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Group Name *</Label>
              <Input 
                value={groupForm.name}
                onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Engineering Team"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={groupForm.description}
                onChange={(e) => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this group"
              />
            </div>
            <div>
              <Label>Shield color</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {GROUP_COLOR_OPTIONS.map((color) => {
                  const selected = groupForm.color === color;
                  return (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setGroupForm(prev => ({ ...prev, color }))}
                      className={`flex h-11 w-11 items-center justify-center rounded-xl border bg-secondary transition-all ${
                        selected
                          ? 'border-primary ring-2 ring-primary/35'
                          : 'border-border hover:border-primary/40'
                      }`}
                      style={{ color }}
                      aria-label={`Use ${color} group shield color`}
                    >
                      <Shield className="h-5 w-5" />
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-3 rounded-xl border border-border bg-secondary/50 p-3">
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-xl border bg-background"
                  style={{
                    color: groupForm.color || DEFAULT_GROUP_COLOR,
                    borderColor: groupForm.color ? `${groupForm.color}55` : 'hsl(var(--border))',
                  }}
                >
                  <Shield className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground">{groupForm.name || 'Group preview'}</p>
                  <p className="text-xs text-muted-foreground">This shield appears in group lists and employee assignment.</p>
                </div>
              </div>
            </div>
          </div>
          {groupFormError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
              {groupFormError}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => handleGroupDialogOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSaveGroup} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Member to {selectedGroup?.name}</DialogTitle>
          </DialogHeader>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={addMemberSearch}
              onChange={(e) => setAddMemberSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {availableMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {addMemberSearch ? 'No matching users found.' : 'All users are already members.'}
              </p>
            ) : (
              availableMembers.map((user) => (
                <div
                  key={user.user_id || user.id}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary/80 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <UserAvatar user={user} size="sm" />
                    <div>
                      <p className="text-sm font-medium">
                        {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                      </p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddMember(user); }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
